import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import haxios from '../lib/haxios';

export default function AddVehicle() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        branch: '',
        registration: '',
        make: '',
        model: '',
        year: '',
        fuel_card_number: '',
    });
    const [branches, setBranches] = useState([]);
    const [error, setError] = useState('');
    const [apiError, setApiError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch branches on component mount
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await haxios.get('/visiondashboard/targets/branches');
                if (response.length > 0) {
                    setBranches(response);
                } else {
                    setApiError('No branches found.');
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
                setApiError('Failed to load branches.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchBranches();
    }, []);

    // Prevent form submission on Enter key
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') e.preventDefault();
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Handle branch selection and automatically set MamCode
    const handleBranchChange = (e) => {
        const selectedBranch = branches.find(branch => branch.Branch === e.target.value);
        if (selectedBranch) {
            setFormData({
                ...formData,
                branch: selectedBranch.Branch,
            });
        } else {
            setFormData({
                ...formData,
                branch: '',
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setApiError('');
        setIsSubmitting(true);

        // Basic validation
        if (!formData.branch || !formData.registration || !formData.make || !formData.model || !formData.year) {
            setError('All fields except fuel card number are required.');
            setIsSubmitting(false);
            return;
        }

        // Ensure `year` is a valid number (fixes MySQL integer error)
        if (isNaN(formData.year) || formData.year < 1900 || formData.year > new Date().getFullYear()) {
            setError('Please enter a valid year.');
            setIsSubmitting(false);
            return;
        }

        try {
            // Direct API call using haxios instead of useApi hook
            const response = await haxios.post('/visiondashboard/salesassist/vehicles', formData);

            if (response.success) {
                navigate('/fleet/vehicles');
            } else {
                setApiError(response.message || 'Failed to add vehicle.');
            }
        } catch (error) {
            console.error('Error adding vehicle:', error);
            setApiError('Server error. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto mt-10 p-6 bg-white rounded-lg shadow">
            <h1 className="text-xl font-bold mb-4">Add New Vehicle</h1>

            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            {apiError && <p className="text-red-500 text-sm mb-4">{apiError}</p>}

            {isLoading ? (
                <p>Loading branches...</p>
            ) : (
                <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="space-y-4">
                    <div>
                        <select
                            name="branch"
                            required
                            className="w-full p-2 border rounded"
                            onChange={handleBranchChange}
                            value={formData.branch}
                        >
                            <option value="">Select Branch</option>
                            {branches.map((branch) => (
                                <option key={branch.Branch} value={branch.Branch}>
                                    {branch.Branch} ({branch.MamCode})
                                </option>
                            ))}
                        </select>
                    </div>
                    <input
                        type="text"
                        name="registration"
                        placeholder="Registration"
                        required
                        className="w-full p-2 border rounded"
                        onChange={handleChange}
                        value={formData.registration}
                    />
                    <input
                        type="text"
                        name="make"
                        placeholder="Make"
                        required
                        className="w-full p-2 border rounded"
                        onChange={handleChange}
                        value={formData.make}
                    />
                    <input
                        type="text"
                        name="model"
                        placeholder="Model"
                        required
                        className="w-full p-2 border rounded"
                        onChange={handleChange}
                        value={formData.model}
                    />
                    <input
                        type="number"
                        name="year"
                        placeholder="Year"
                        required
                        className="w-full p-2 border rounded"
                        onChange={handleChange}
                        value={formData.year}
                        min="1900"
                        max={new Date().getFullYear()}
                    />
                    <input
                        type="text"
                        name="fuel_card_number"
                        placeholder="Fuel Card Number"
                        className="w-full p-2 border rounded"
                        onChange={handleChange}
                        value={formData.fuel_card_number}
                    />

                    <button
                        type="submit"
                        className={`w-full p-2 rounded text-white ${isSubmitting ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Adding...' : 'Add Vehicle'}
                    </button>
                </form>
            )}
        </div>
    );
}